import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Download, Menu, FileText, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import { toast } from 'sonner';

// Section components
import BrandOverview from '../components/sections/BrandOverview';
import LogoGuidelines from '../components/sections/LogoGuidelines';
import ColorPalette from '../components/sections/ColorPalette';
import Typography from '../components/sections/Typography';
import ImageryStyle from '../components/sections/ImageryStyle';
import GraphicElements from '../components/sections/GraphicElements';
import BrandUIKit from '../components/sections/BrandUIKit';
import LayoutComposition from '../components/sections/LayoutComposition';
import ToneOfVoice from '../components/sections/ToneOfVoice';
import SocialMediaGuidelines from '../components/sections/SocialMediaGuidelines';
import MarketingCollateral from '../components/sections/MarketingCollateral';
import DosAndDonts from '../components/sections/DosAndDonts';
import BrandRefinement from '../components/sections/BrandRefinement';
import BrandSummary from '../components/sections/BrandSummary';

const sections = [
  { id: 'overview', title: 'Brand Overview', component: BrandOverview },
  { id: 'logo', title: 'Logo Guidelines', component: LogoGuidelines },
  { id: 'colors', title: 'Color Palette', component: ColorPalette },
  { id: 'typography', title: 'Typography', component: Typography },
  { id: 'imagery', title: 'Imagery & Photography', component: ImageryStyle },
  { id: 'graphics', title: 'Graphic Elements', component: GraphicElements },
  { id: 'uikit', title: 'UI Kit', component: BrandUIKit },
  { id: 'layout', title: 'Layout & Composition', component: LayoutComposition },
  { id: 'voice', title: 'Tone of Voice', component: ToneOfVoice },
  { id: 'social', title: 'Social Media', component: SocialMediaGuidelines },
  { id: 'collateral', title: 'Marketing Collateral', component: MarketingCollateral },
  { id: 'dosdonts', title: "Do's & Don'ts", component: DosAndDonts },
  { id: 'refinement', title: 'Brand Refinement', component: BrandRefinement },
  { id: 'summary', title: 'Quick Reference', component: BrandSummary },
];

export default function BrandStyleGuide() {
  const [activeSection, setActiveSection] = useState('overview');
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  const handleDownloadPDF = async () => {
    setIsGeneratingPDF(true);
    toast.loading('Generating PDF guide...');
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/generate-brand-pdf`, {
        method: 'POST',
      });
      
      if (!response.ok) {
        throw new Error('Failed to generate PDF');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'jackstone-brand-style-guide.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.dismiss();
      toast.success('Brand Style Guide PDF downloaded!');
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.dismiss();
      toast.error('Failed to generate PDF. Please try again.');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const scrollToSection = (id) => {
    setActiveSection(id);
    const element = document.getElementById(id);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border"
      >
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo-blue.png" alt="Jackstone" className="h-8" />
            <div className="hidden sm:block h-6 w-px bg-border"></div>
            <div className="hidden sm:block text-sm text-subtle font-medium">
              Brand Style Guide
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Link to="/templates">
              <Button
                variant="ghost"
                size="sm"
                className="hidden md:flex gap-2"
              >
                <FileText className="h-4 w-4" />
                Templates
              </Button>
            </Link>

            <Link to="/compliance">
              <Button
                variant="ghost"
                size="sm"
                className="hidden md:flex gap-2"
                data-testid="header-compliance-link"
              >
                <ShieldCheck className="h-4 w-4" />
                Compliance
              </Button>
            </Link>
            
            <Button
              onClick={handleDownloadPDF}
              variant="outline"
              size="sm"
              className="hidden md:flex gap-2"
              disabled={isGeneratingPDF}
            >
              <Download className="h-4 w-4" />
              {isGeneratingPDF ? 'Generating...' : 'Download PDF'}
            </Button>
            
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="outline" size="icon" className="lg:hidden">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-80">
                <nav className="mt-8">
                  <ul className="space-y-1">
                    {sections.map((section) => (
                      <li key={section.id}>
                        <button
                          onClick={() => scrollToSection(section.id)}
                          className={`w-full text-left px-4 py-2 rounded-md transition-smooth ${
                            activeSection === section.id
                              ? 'bg-jackstone-blue text-white'
                              : 'hover:bg-light-blue'
                          }`}
                        >
                          {section.title}
                        </button>
                      </li>
                    ))}
                    <li className="pt-4 border-t border-border mt-4">
                      <Link to="/templates" className="block">
                        <button className="w-full text-left px-4 py-2 rounded-md transition-smooth hover:bg-light-blue flex items-center gap-2">
                          <FileText className="h-4 w-4" />
                          Document Templates
                        </button>
                      </Link>
                    </li>
                    <li>
                      <Link to="/compliance" className="block">
                        <button className="w-full text-left px-4 py-2 rounded-md transition-smooth hover:bg-light-blue flex items-center gap-2">
                          <ShieldCheck className="h-4 w-4" />
                          Compliance Checker
                        </button>
                      </Link>
                    </li>
                  </ul>
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </motion.header>

      <div className="flex pt-16">
        <motion.aside
          initial={{ x: -100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="hidden lg:block fixed left-0 top-16 bottom-0 w-72 border-r border-border bg-card overflow-y-auto"
        >
          <nav className="p-6">
            <ul className="space-y-1">
              {sections.map((section) => (
                <li key={section.id}>
                  <button
                    onClick={() => scrollToSection(section.id)}
                    className={`w-full text-left px-4 py-3 rounded-lg transition-smooth text-sm ${
                      activeSection === section.id
                        ? 'bg-jackstone-blue text-white font-medium'
                        : 'text-body hover:bg-light-blue'
                    }`}
                  >
                    {section.title}
                  </button>
                </li>
              ))}
            </ul>
            
            {/* Templates Link */}
            <div className="mt-6 pt-6 border-t border-border">
              <Link to="/templates">
                <button className="w-full text-left px-4 py-3 rounded-lg transition-smooth text-sm text-body hover:bg-light-blue flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Document Templates
                </button>
              </Link>
            </div>
          </nav>
        </motion.aside>

        <main className="flex-1 lg:ml-72">
          <div className="container mx-auto px-4 py-12 max-w-5xl">
            {sections.map((section, index) => {
              const SectionComponent = section.component;
              return (
                <motion.section
                  key={section.id}
                  id={section.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-100px' }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="mb-24 scroll-mt-20"
                >
                  <SectionComponent />
                </motion.section>
              );
            })}
          </div>
        </main>
      </div>
    </div>
  );
}
